import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Inline TOTP challenge form, shown after a password sign-in when the
 * account has a verified MFA factor. Calls onVerified() once the session
 * is elevated to AAL2.
 */
export function TwoFactorChallenge({
  onVerified,
  onCancel,
}: {
  onVerified: () => void;
  onCancel?: () => void;
}) {
  const [factorId, setFactorId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: listErr } = await supabase.auth.mfa.listFactors();
      if (cancelled) return;
      const factor = (data?.totp ?? []).find((f) => f.status === "verified");
      if (listErr || !factor) {
        setError(listErr?.message ?? "No two-factor method found");
        setLoading(false);
        return;
      }
      const { data: chal, error: chalErr } = await supabase.auth.mfa.challenge({ factorId: factor.id });
      if (cancelled) return;
      if (chalErr || !chal) {
        setError(chalErr?.message ?? "Could not start challenge");
        setLoading(false);
        return;
      }
      setFactorId(factor.id);
      setChallengeId(chal.id);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!factorId || !challengeId) return;
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your authenticator app");
      return;
    }
    setLoading(true);
    setError(null);
    const { error: verErr } = await supabase.auth.mfa.verify({ factorId, challengeId, code });
    if (verErr) {
      setLoading(false);
      setError(verErr.message);
      return;
    }
    onVerified();
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label htmlFor="mfa-challenge-code" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">
          Two-factor code
        </label>
        <Input
          id="mfa-challenge-code"
          inputMode="numeric"
          maxLength={6}
          autoFocus
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="123456"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Open your authenticator app and enter the 6-digit code.
        </p>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={loading || code.length !== 6}>
          {loading ? "Verifying…" : "Verify"}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

/** Returns true if the current session is AAL1 but needs to elevate to AAL2. */
export async function needsMfaChallenge(): Promise<boolean> {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) return false;
  return data.currentLevel === "aal1" && data.nextLevel === "aal2";
}